import { FC, useEffect, useState } from 'react';
import { FiCopy } from 'react-icons/fi';
import { useHistory } from 'react-router-dom';
import { truncate } from 'utils/address';
import { IoIosArrowRoundBack } from 'react-icons/io';
import { RiExternalLinkLine } from 'react-icons/ri';
import { EtherscanLinkType, getEtherscanLink } from 'utils/getEtherscanLink';
import { useWeb3React } from 'hooks/web3';
import { FaTwitter } from 'react-icons/fa';
import {
  Box,
  Heading,
  Flex,
  IconButton,
  HStack,
  Text,
  useClipboard,
  Link as ChakraLink,
  useToast,
  Stack,
  Button,
  Skeleton
} from '@chakra-ui/react';
import CrucibleImage from './CrucibleImage';
import { BiUser } from 'react-icons/bi';

type Props = {
  crucibleId: string;
  owner: string;
};

const CrucibleInformation: FC<Props> = ({ crucibleId, owner }) => {
  const toast = useToast();
  const history = useHistory();

  const { chainId = 1, provider } = useWeb3React();
  const { hasCopied, onCopy } = useClipboard(crucibleId || '');
  const { hasCopied: hasCopiedOwner, onCopy: onCopyOwner } = useClipboard(
    owner || ''
  );
  const [ens, setEns] = useState<string | null>(null);
  const [ensLoading, setEnsLoading] = useState(true);

  useEffect(() => {
    provider
      .lookupAddress(owner)
      .then(
        (v) => setEns(v),
        (e) => console.log(e)
      )
      .finally(() => setEnsLoading(false));
  }, [owner, provider]);

  useEffect(() => {
    const hasCopiedCrucibleAddress = hasCopied;
    const hasCopiedOwnerAddress = hasCopiedOwner;

    if (hasCopiedCrucibleAddress || hasCopiedOwnerAddress) {
      toast({
        title: `Copied ${
          hasCopiedCrucibleAddress ? 'Crucible' : 'Owner'
        } address`,
        status: 'info',
        duration: 4000,
        isClosable: true
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasCopied, hasCopiedOwner]);

  return (
    <Flex direction={'column'} alignItems='center'>
      <Box
        mb={4}
        top={[0, 0, 0, 5]}
        position={['relative', null, null, 'absolute']}
      >
        <Button
          onClick={() => history.goBack()}
          variant='ghost'
          backgroundColor={['none', null, null, 'whiteAlpha.200']}
          _hover={{ bg: ['whiteAlpha.200', null, null, 'gray.800'] }}
        >
          <HStack>
            <IoIosArrowRoundBack fontSize='32px' />
            <Text>Explore</Text>
          </HStack>
        </Button>
      </Box>
      <CrucibleImage address={crucibleId} />
      <Box>
        <Heading mt={[5]} fontSize='2xl' textAlign='center'>
          {truncate(crucibleId)}
        </Heading>
      </Box>
      <Box>
        <HStack mt={3} mb={1}>
          <IconButton
            isRound
            bg='whiteAlpha.50'
            variant='ghost'
            aria-label='copy'
            onClick={onCopy}
            icon={<FiCopy />}
          />
          <ChakraLink
            href={getEtherscanLink(
              chainId,
              crucibleId,
              EtherscanLinkType.ADDRESS
            )}
            isExternal
          >
            <IconButton
              isRound
              size='sm'
              bg='whiteAlpha.50'
              variant='ghost'
              aria-label='etherscan'
              icon={<RiExternalLinkLine />}
            />
          </ChakraLink>
          <ChakraLink
            href={
              chainId === 1 || window.location.href.includes('network')
                ? `https://twitter.com/intent/tweet?text=${window.location.href}`
                : `https://twitter.com/intent/tweet?text=${window.location.href}?network=${chainId}`
            }
            isExternal
          >
            <IconButton
              isRound
              size='sm'
              bg='whiteAlpha.50'
              variant='ghost'
              aria-label='share'
              icon={<FaTwitter />}
            />
          </ChakraLink>
        </HStack>
      </Box>
      <Box>
        <Text variant='h6' align='center'>
          <Stack direction='row' alignItems='center' justifyContent='center'>
            <Button variant='ghost' onClick={onCopyOwner}>
              <BiUser />
              {ensLoading && <Skeleton width={100} height={4} />}
              {!ensLoading && ens && <span>{ens}</span>}
              {!ensLoading && !ens && <span>{truncate(owner)}</span>}
            </Button>
          </Stack>
        </Text>
      </Box>
    </Flex>
  );
};

export default CrucibleInformation;
